import { useMemo } from 'react';
import { Paper, Title, Text, Stack, SimpleGrid } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import type { DataLibrarySurveyRow } from '../../types';
import { buildCohortStageBars, getSatisfactionSeries } from './chartUtils';

interface MidpointEndpointChartProps {
  title: string;
  rows: DataLibrarySurveyRow[];
  field: 'overall_satisfaction' | 'coach_satisfaction';
  onSegmentClick: (cohort: string, stage: 'Midpoint' | 'Endpoint', rating: string) => void;
}

export function MidpointEndpointChart({
  title,
  rows,
  field,
  onSegmentClick,
}: MidpointEndpointChartProps) {
  const midpointData = useMemo(() => buildCohortStageBars(rows, 'Midpoint', field), [rows, field]);
  const endpointData = useMemo(() => buildCohortStageBars(rows, 'Endpoint', field), [rows, field]);
  const series = useMemo(() => getSatisfactionSeries(field), [field]);

  const renderBar = (data: Array<Record<string, string | number>>, stage: 'Midpoint' | 'Endpoint', label: string) => {
    if (data.length === 0) {
      return (
        <Paper p="lg" radius="md" withBorder>
          <Title order={5} mb="md">{label}</Title>
          <Text c="dimmed" ta="center" py="xl">No data</Text>
        </Paper>
      );
    }

    return (
      <Paper p="lg" radius="md" withBorder>
        <Title order={5} mb="md">{label}</Title>
        <BarChart
          h={300}
          data={data}
          dataKey="cohort"
          type="stacked"
          series={series}
          withLegend
          legendProps={{ verticalAlign: 'top', align: 'right' }}
          barProps={(seriesItem) => ({
            onClick: (entry: Record<string, unknown>) => {
              const cohort = entry.cohort as string;
              if (cohort) onSegmentClick(cohort, stage, seriesItem.name);
            },
            style: { cursor: 'pointer' },
          })}
          tooltipProps={{
            content: ({ payload, label: cohortLabel }) => {
              if (!payload?.length) return null;
              return (
                <Paper p="sm" radius="sm" withBorder shadow="sm" style={{ backgroundColor: '#fff' }}>
                  <Stack gap={4}>
                    <Text fw={600} size="sm">{cohortLabel}</Text>
                    <Text size="xs" c="dimmed">{stage}</Text>
                    {payload.filter(p => Number(p.value) > 0).map(p => (
                      <Text key={p.name} size="xs">
                        <span style={{ color: p.color as string }}>{'\u25CF'} </span>
                        {p.name}: {p.value}
                      </Text>
                    ))}
                  </Stack>
                </Paper>
              );
            },
          }}
          xAxisProps={{ angle: -45, textAnchor: 'end', height: 80, interval: 0, tick: { fontSize: 10 } }}
        />
      </Paper>
    );
  };

  return (
    <Stack gap="md">
      <SimpleGrid cols={2}>
        {renderBar(midpointData, 'Midpoint', `Midpoint — ${title}`)}
        {renderBar(endpointData, 'Endpoint', `Endpoint — ${title}`)}
      </SimpleGrid>
    </Stack>
  );
}
